import React, { useCallback, useState } from "react";
import { BemHelper } from "@/utils/BemHelper";
import classNames from "classnames";
import { ChevronLeftIcon } from "@/icons/primary/chevron-left-icon";
import { ChevronRightIcon } from "@/icons/primary/chevron-right-icon";
import { Theme, ThemeIntent } from "../types";
import { Carousel } from "antd";
import { THEMES, THEME_INTENTS } from "../constants";
import { useActiveBreakpoints } from "@/utils/useActiveBreakpoints";

export type CtCarouselProps = {
  slides: any[];
  spaceBetween?: number;
  renderSlide: (slide: any) => React.ReactNode;
  slidesToShow: number;
  className?: string;
  onActiveIndexChange?: (index: number) => void;
  onArrowClick?: (direction: "left" | "right") => void;
  theme?: Theme;
  intent?: ThemeIntent;
  centerMode?: boolean;
  centerPadding?: string;
  speed?: number;
  infinite?: boolean;
  responsive?: any[];
  adaptiveHeight?: boolean;
};

const bem = new BemHelper("ct-carousel");

function CtCarousel({
  slides,
  spaceBetween,
  renderSlide,
  className,
  onActiveIndexChange,
  onArrowClick,
  theme,
  intent,
  slidesToShow,
  centerMode,
  centerPadding,
  speed,
  infinite,
  responsive,
  adaptiveHeight,
}: CtCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = React.useRef<any>(null);
  const isLeftArrowDisabled = activeIndex === 0 && !infinite;
  const isRightArrowDisabled = activeIndex === slides.length - slidesToShow && !infinite;
  const { activeBreakpoints } = useActiveBreakpoints();

  const handleActiveIndexChange = (currentIndex: number) => {
    setActiveIndex(currentIndex);
    onActiveIndexChange?.(currentIndex);
  };

  const handleArrowClick = useCallback(
    (direction: "left" | "right") => {
      if (direction === "left") {
        carouselRef.current.prev();
      }

      if (direction === "right") {
        carouselRef.current.next();
      }

      onArrowClick?.(direction);
    },
    [onArrowClick],
  );

  return (
    <div
      className={classNames(
        bem.block(),
        className,
        !activeBreakpoints.md && bem.modifier("mobile"),
      )}
    >
      <button
        className={classNames(
          bem.element("arrow"),
          bem.modifier("left", "arrow"),
          bem.modifier(theme || THEMES.THEME_1, "arrow"),
          bem.modifier(intent || THEME_INTENTS.DEFAULT, "arrow"),
          isLeftArrowDisabled && bem.modifier("disabled", "arrow"),
          !activeBreakpoints.md && bem.modifier("mobile", "arrow"),
        )}
        onClick={() => handleArrowClick("left")}
        disabled={isLeftArrowDisabled}
      >
        <div className={bem.element("arrow-icon")}>
          <ChevronLeftIcon />
        </div>
      </button>

      <button
        className={classNames(
          bem.element("arrow"),
          bem.modifier("right", "arrow"),
          bem.modifier(theme || THEMES.THEME_1, "arrow"),
          bem.modifier(intent || THEME_INTENTS.DEFAULT, "arrow"),
          isRightArrowDisabled && bem.modifier("disabled", "arrow"),
          !activeBreakpoints.md && bem.modifier("mobile", "arrow"),
        )}
        onClick={() => handleArrowClick("right")}
        disabled={isRightArrowDisabled}
      >
        <div className={bem.element("arrow-icon")}>
          <ChevronRightIcon />
        </div>
      </button>

      <Carousel
        ref={(node) => (carouselRef.current = node)}
        dots={false}
        slidesToShow={slidesToShow}
        centerMode={centerMode}
        centerPadding={centerPadding}
        speed={speed}
        infinite={infinite}
        responsive={responsive}
        afterChange={handleActiveIndexChange}
        adaptiveHeight={adaptiveHeight}
      >
        {slides.map((slide, index) => (
          <div key={index}>
            <div
              style={{
                padding: `0 ${spaceBetween}px`,
              }}
            >
              {renderSlide(slide)}
            </div>
          </div>
        ))}
      </Carousel>
    </div>
  );
}

CtCarousel.defaultProps = {
  className: undefined,
  spaceBetween: 16,
  onActiveIndexChange: undefined,
  onArrowClick: undefined,
  theme: THEMES.THEME_1,
  intent: THEME_INTENTS.DEFAULT,
  centerMode: false,
  centerPadding: "50px",
  speed: 500,
  infinite: false,
  responsive: [],
  slidesToShow: 1,
  adaptiveHeight: false,
};

export default CtCarousel;
